// A typed ask — a plain question, or a `kind:"concern"` — pinned on the issue
// hub.
//
// The typed-ask channel (`question_pending` → `question_answer`) exists because
// a readiness card cannot carry a question before the card exists, and a
// question answered in prose is unattributable. The Focus wizard pins those asks
// as a step; this host had NO renderer for them at all, so an ask raised while
// the user sat on the issue page was durable, folded into the timeline, and
// invisible — the user saw nothing while the agent waited for an answer. A
// concern nobody is shown is the same failure as prose that gets dropped.
//
// ONE ask on screen at a time, like the Focus pin. Its answers — typed, or a
// click on Loom's answer — and its skips join the issue page's HELD batch with
// the card's rows, and go out together as one `question_answer` frame (the
// caller wires it), never as chat prose: that frame is what attributes the bytes
// to THIS ask and retires it server-side.
//
// A plain ask with a draft shows it CLOSED, like a card row. A CONCERN shows what
// the field says now, then — when it carries one — Loom's suggested reply,
// closed, then Keep / Change of equal weight — never the field's value offered
// back as "Loom's draft".

import { useRef, useState, type JSX } from "react";

import { uiProse } from "@loom/shared/brand";
import { LoomBlock, SlotWidget, UseButton } from "../../chat/render/ReadinessCard";
import { defaultWidgetForField } from "../../chat/render/answerMessage";
import {
  isValueField,
  loomAnswerOf,
  useArmedClick,
} from "../../chat/render/loomAnswer";
import type { TimelineEntry } from "../../chat/useWsSession";
import { reportClientEvent } from "../../lib/client-trace";
import { skipLabelKey, useFieldCopy } from "../../focus/copy";
import { useT, usePlural } from "../../i18n/index.js";
import { CHIP_BTN } from "../../ui/classes.js";

/** One open ask, flattened out of the timeline entry. */
export interface OpenAsk {
  readonly askId: string;
  /** The readiness field this settles, or null for a free-standing ask. */
  readonly field: string | null;
  readonly question: string;
  readonly help?: string;
  readonly optional: boolean;
  /** `askKind === "concern"` — Loom flagging a problem with something already
   *  settled, rather than asking for something missing. Always skippable. */
  readonly concern: boolean;
  /** Loom's draft answer rows for this ask, shown by the control as Loom's
   *  (`loomAnswerOf` → `SlotWidget`'s `loomAnswer`, shown closed). On a concern,
   *  one row: Loom's suggested reply (`PinConcern`). */
  readonly draft?: ReadonlyArray<string>;
}

/**
 * Open typed asks for one issue, in arrival order.
 *
 * Mirrors RefineFlow's `typedAsks` derivation exactly — same suppressions
 * (server-side `resolved`, the caller's locally-handled set), same empty-question
 * tombstone guard (see `applyEvent`'s resolution-ahead-of-pending branch), same
 * deliberate independence from the readiness card, since the whole point of the
 * channel is that it works before a card exists. Kept separate from that one
 * because RefineFlow's is entangled with the Focus step model (queue, held
 * answers, pin reconciliation) that this host does not have.
 */
export const openAsksFor = (
  timeline: ReadonlyArray<TimelineEntry>,
  iid: number | null,
  handled: ReadonlySet<string>,
): ReadonlyArray<OpenAsk> => {
  if (iid === null) return [];
  const out: OpenAsk[] = [];
  for (const e of timeline) {
    if (e.kind !== "agent_ask") continue;
    if (e.issueIid !== iid) continue;
    if (e.resolved) continue;
    if (handled.has(e.askId)) continue;
    // The design SKETCH is not a free-text question and must never be rendered
    // as one here. It is answered by a BUTTON, on a frame that carries no text
    // at all, precisely so there are no user bytes for the readiness gate's
    // word-boundary-free quote scan to match. In the pin it became a text box
    // whose answer went out on `question_answer` — which `stream.ts` echoes onto
    // the bus as a `user_message`, putting the user's own "yes" straight into
    // the log the gate reads. It also showed the ask's English fallback sentence
    // in a German session, and asked about a screen with no picture on it.
    // `previewAskFor` is the other half: it renders the real widget here.
    //
    // Matched on the MEMBER, not on `subject !== undefined`: `AgentAsk.subject`
    // is documented as a rendering instruction a surface that does not know a
    // member must fall back to `question` for, so a future kind still belongs in
    // this list rather than nowhere.
    if (e.subject?.kind === "figma_preview") continue;
    if (e.question.trim() === "") continue; // tombstone
    out.push({
      askId: e.askId,
      field: e.field,
      question: e.question,
      ...(e.help !== undefined ? { help: e.help } : {}),
      optional: e.optional === true,
      concern: e.askKind === "concern",
      ...(e.draft !== undefined && e.draft.length > 0 ? { draft: e.draft } : {}),
    });
  }
  return out;
};

/** One open design-sketch ask: the handle, and the ask it retires. */
export interface OpenPreviewAsk {
  readonly askId: string;
  readonly previewId: string;
}

/**
 * The open design-sketch question for one issue, or `null`.
 *
 * The same fold `openAsksFor` runs and the exact complement of its
 * `subject` guard, so a preview ask is rendered by exactly one of the two —
 * never both, never neither. The NEWEST one wins: the ask is raised once per
 * frame per session, so two open at once means two different reads, and the
 * later read is the one the agent is working from.
 *
 * Deliberately independent of the readiness card, like every typed ask: a
 * design is read long before a card exists.
 */
export const previewAskFor = (
  timeline: ReadonlyArray<TimelineEntry>,
  iid: number | null,
  handled: ReadonlySet<string>,
): OpenPreviewAsk | null => {
  if (iid === null) return null;
  let found: OpenPreviewAsk | null = null;
  for (const e of timeline) {
    if (e.kind !== "agent_ask") continue;
    if (e.issueIid !== iid) continue;
    if (e.resolved) continue;
    if (handled.has(e.askId)) continue;
    if (e.subject?.kind !== "figma_preview") continue;
    // NOT gated on a non-empty `question`: the headline this ask renders is
    // Loom's own catalog string (`focus.figma.preview.question`), never the
    // ask's English fallback, so an empty one is not the tombstone it is for a
    // plain question. `resolved` / `handled` above already cover retirement.
    found = { askId: e.askId, previewId: e.subject.previewId };
  }
  return found;
};

interface AgentAskPinProps {
  readonly asks: ReadonlyArray<OpenAsk>;
  /** Project context for a milestone-bound ask's dropdown. */
  readonly projectId?: number;
  readonly onAnswer: (ask: OpenAsk, text: string) => void;
  readonly onSkip: (ask: OpenAsk) => void;
  /** The card's value for a field, shown under a concern as what it would
   *  keep. `null` when the card has none. */
  readonly currentValueFor?: (field: string) => string | null;
}

/**
 * A concern on the pin: what the field says now, then Loom's suggested reply
 * when the concern carries one (closed — "Use this text" makes it the user's
 * reply), then Keep / Change of equal weight. "Keep it as it is" is the decline
 * and sends no bytes; "Change it" opens the field's control EMPTY, and its reply
 * goes out as a concern reply — as does a click on Loom's reply.
 */
function PinConcern({
  ask,
  currentValue,
  projectId,
  answerLabel,
  onKeep,
  onReply,
}: {
  readonly ask: OpenAsk;
  readonly currentValue: string | null;
  readonly projectId?: number;
  readonly answerLabel?: string;
  readonly onKeep: () => void;
  readonly onReply: (text: string) => void;
}) {
  const t = useT();
  const [changing, setChanging] = useState(false);
  const armed = useArmedClick();
  // Loom's suggested reply — but NOT for a milestone: its only answers are a
  // pick from the list ("Change it" opens it) or Keep, and Loom's words are
  // neither (the server would not take them as the milestone either).
  const reply =
    ask.field === "milestone"
      ? null
      : loomAnswerOf(
          ask.field ?? "agent_ask",
          {
            concern: true,
            ...(currentValue !== null ? { value: currentValue } : {}),
            ...(ask.draft !== undefined ? { draft: ask.draft } : {}),
          },
          { settled: false },
        );
  const keep = (
    <button
      type="button"
      className={CHIP_BTN}
      onClick={(e) => {
        if (!armed(e)) return;
        onKeep();
      }}
    >
      {t(skipLabelKey({ field: ask.field, concern: true }))}
    </button>
  );
  return (
    <>
      {ask.field !== null && currentValue !== null && (
        <div className="mt-1 text-xs">
          <p className="font-medium text-ink-subtle">{t("focus.refine.concern.current")}</p>
          <p className="whitespace-pre-wrap break-words text-ink-3">{currentValue}</p>
        </div>
      )}
      {!changing && reply !== null && reply.kind === "text" && (
        <div className="mt-1">
          <LoomBlock text={reply.text}>
            <div className="mt-1 flex flex-wrap gap-1">
              <UseButton
                label={t("focus.refine.suggest.adopt")}
                {...(answerLabel !== undefined
                  ? { ariaLabel: t("focus.refine.suggest.adoptAria", { label: answerLabel }) }
                  : {})}
                onUse={() => {
                  reportClientEvent("ui.click", {
                    control: "loom_answer_use",
                    surface: "issue_pin_concern",
                  });
                  onReply(reply.text);
                }}
              />
            </div>
          </LoomBlock>
        </div>
      )}
      {changing ? (
        <>
          <SlotWidget
            field={ask.field ?? "agent_ask"}
            widget={defaultWidgetForField(ask.field ?? "agent_ask")}
            {...(projectId !== undefined ? { projectId } : {})}
            onAnswer={onReply}
            draftKey={`issueAsk:${ask.askId}`}
          />
          <div className="mt-2">{keep}</div>
        </>
      ) : (
        <div className="mt-2 flex flex-wrap gap-1">
          {keep}
          <button type="button" className={CHIP_BTN} onClick={() => setChanging(true)}>
            {ask.field !== null
              ? t("focus.refine.concern.change")
              : t("focus.refine.concern.reply")}
          </button>
        </div>
      )}
    </>
  );
}

export function AgentAskPin({
  asks,
  projectId,
  onAnswer,
  onSkip,
  currentValueFor,
}: AgentAskPinProps): JSX.Element | null {
  const t = useT();
  const tn = usePlural();
  const fieldCopy = useFieldCopy();
  // Guard a double-fire (a fast double-click on Send / Skip): the parent marks
  // the ask handled, but two clicks inside ONE render frame both read the
  // pre-click state — and each would be its own agent turn for one question.
  // A ref, not state, for exactly that reason: it is current within the frame.
  const sentRef = useRef<string | null>(null);
  const ask = asks[0];
  if (ask === undefined) return null;
  const copy = ask.field !== null ? fieldCopy(ask.field) : null;
  // The agent's own help wins; a spec-bound ask falls back to the field's
  // standing help. Gated on the TRIMMED text, so an ask that carried `help: ""`
  // still gets the field copy instead of an empty line.
  const helpText =
    (ask.help ?? "").trim() !== "" ? ask.help! : (copy?.help ?? "").trim();
  const queued = asks.length - 1;
  const act = (fn: () => void): void => {
    if (sentRef.current === ask.askId) return;
    sentRef.current = ask.askId;
    fn();
  };
  return (
    <div
      className="mb-2 rounded-lg border border-caution-line bg-caution-soft p-3 text-sm"
      data-testid="agent-ask-pin"
    >
      {/* A concern names ITSELF, never the field it is about: the field's own
          kicker ("Who is it for?") would read as a re-ask of a question the user
          has already answered, which is the one thing a concern is not. */}
      <p className="text-xs font-medium tracking-wide text-caution-fg uppercase">
        {ask.concern
          ? t("focus.refine.concern.kicker")
          : (copy?.kicker ?? t("focus.field.fallback.kicker"))}
      </p>
      <p className="mt-1 font-medium text-ink-2">{uiProse(ask.question)}</p>
      {helpText !== "" && <p className="mt-0.5 text-xs text-ink-muted">{helpText}</p>}
      {ask.concern ? (
        <PinConcern
          key={ask.askId}
          ask={ask}
          currentValue={ask.field !== null ? (currentValueFor?.(ask.field) ?? null) : null}
          {...(projectId !== undefined ? { projectId } : {})}
          {...(copy !== null ? { answerLabel: copy.label } : {})}
          onKeep={() => act(() => onSkip(ask))}
          onReply={(text) => act(() => onAnswer(ask, text))}
        />
      ) : (
        <SlotWidget
          // REMOUNT the control per ask. `useRemountSafeState` reads its store in
          // the initializer only, so swapping `draftKey` under a live component
          // would keep the previous question's half-typed text on screen AND
          // write it under the new ask's key — one question's answer leaking into
          // the next.
          key={ask.askId}
          // A field-less ask has no purpose-built control — free text is the
          // honest default. A spec-bound one reuses the field's own widget, so the
          // answer goes out with the same self-label the card's widget would send.
          field={ask.field ?? "agent_ask"}
          widget={defaultWidgetForField(ask.field ?? "agent_ask")}
          {...(projectId !== undefined ? { projectId } : {})}
          onAnswer={(text) => act(() => onAnswer(ask, text))}
          // askId-keyed, NOT field-keyed: two consecutive field-less asks would
          // otherwise share a draft and the second would open pre-filled with the
          // answer to the first.
          draftKey={`issueAsk:${ask.askId}`}
          // Loom's draft for this ask, shown CLOSED — one click is the answer.
          loomAnswer={loomAnswerOf(
            ask.field ?? "agent_ask",
            { ...(ask.draft !== undefined ? { draft: ask.draft } : {}) },
            { settled: false },
          )}
          // A bare yes is refused only where the answer SETS a value; a
          // field-less ask is a yes/no or scoping question, and "ja" answers it.
          guardAssent={ask.field !== null && isValueField(ask.field)}
          {...(copy !== null ? { answerLabel: copy.label } : {})}
          traceSurface="issue_pin"
        />
      )}
      {/* An OPTIONAL plain ask is skippable. (A concern is always skippable —
          its Keep is part of the pair above.) */}
      {ask.optional && !ask.concern && (
        <button
          type="button"
          onClick={() => act(() => onSkip(ask))}
          className={`mt-2 ${CHIP_BTN}`}
        >
          {t(skipLabelKey({ field: ask.field, concern: ask.concern }))}
        </button>
      )}
      {queued > 0 && (
        <p className="mt-2 text-xs text-ink-subtle">
          {tn("plural.askQueue", queued)}
        </p>
      )}
    </div>
  );
}
